import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/Button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ShieldAlert, ExternalLink, ArrowLeft, Sparkles } from 'lucide-react';
import forbidden403Illustration from '@/assets/illustrations/forbidden-403.svg';

export default function Forbidden() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const isSuperAdmin = user?.role === 'super_admin';

  // Auto-logout super-admins so the next sign-in attempt isn't already in their wrong session.
  useEffect(() => {
    if (isSuperAdmin) {
      const t = setTimeout(() => logout(), 200);
      return () => clearTimeout(t);
    }
  }, [isSuperAdmin, logout]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg bg-paper-noise">
      {/* Decorative glows */}
      <div className="pointer-events-none absolute -right-32 -top-40 h-[28rem] w-[28rem] rounded-full bg-gold-gradient-soft blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-[-10rem] h-[24rem] w-[24rem] rounded-full bg-gold-gradient-soft blur-3xl" />

      {/* Header */}
      <div className="absolute right-5 top-5 z-20"><ThemeToggle /></div>
      <div className="absolute left-7 top-7 z-20 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold-gradient text-on-primary shadow-glow">
          <Sparkles size={16} strokeWidth={2.5} />
        </div>
        <div className="font-display text-[15px] font-bold tracking-tight text-text-primary">SlightorCRM</div>
      </div>

      <div className="relative flex min-h-screen items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl rounded-3xl border border-border bg-surface/85 p-8 shadow-elevated backdrop-blur-xl sm:p-10">
          {/* Illustration */}
          <img src={forbidden403Illustration} alt="" aria-hidden width={120} height={120} className="mb-2 opacity-90 select-none pointer-events-none" />

          {/* Status badge */}
          <div className="inline-flex items-center gap-2 rounded-full border border-warning/30 bg-warning-soft px-3 py-1">
            <ShieldAlert size={13} className="text-warning" />
            <span className="text-xs font-semibold uppercase tracking-wide text-warning">403 Forbidden</span>
          </div>

          {/* Heading */}
          <h1 className="mt-5 font-display text-[32px] font-bold leading-tight tracking-tight text-text-primary">
            {isSuperAdmin ? "You're signed in as super-admin." : 'You don\'t have access to this area.'}
          </h1>

          <p className="mt-3 text-[15px] leading-relaxed text-text-secondary">
            {isSuperAdmin ? (
              <>
                SlightorCRM is for company users. Super-admin tools live in a separate console where you manage
                tenants, plans, subscriptions, and AI providers.
              </>
            ) : (
              <>
                The page you tried to open is restricted by role. Sign in with a workspace account that has access,
                or head back to the dashboard.
              </>
            )}
          </p>

          {/* Action panel */}
          {isSuperAdmin ? (
            <div className="mt-7 flex items-center justify-between rounded-2xl border border-primary/30 bg-gold-gradient-soft p-5">
              <div>
                <div className="text-2xs font-semibold uppercase tracking-[0.16em] text-primary">Super-admin Console</div>
                <div className="mt-1 font-mono text-[15px] font-semibold text-text-primary">
                  http://localhost:3010
                </div>
              </div>
              <Button
                onClick={() => { logout(); window.location.href = 'http://localhost:3010'; }}
                iconRight={<ExternalLink size={15} />}
              >
                Open admin
              </Button>
            </div>
          ) : (
            <div className="mt-7 flex flex-col gap-2 sm:flex-row">
              <Button onClick={() => nav('/dashboard', { replace: true })} iconLeft={<ArrowLeft size={15} />}>
                Back to dashboard
              </Button>
              <Button
                variant="secondary"
                onClick={() => { logout(); nav('/login', { replace: true }); }}
              >
                Sign out
              </Button>
            </div>
          )}

          {/* Hint footer */}
          <div className="mt-8 border-t border-border pt-5 text-xs text-text-muted">
            If you think this is a mistake, contact your workspace admin or check your role under{' '}
            <span className="font-medium text-text-secondary">Settings → Profile</span>.
          </div>
        </div>
      </div>
    </div>
  );
}
